import React, { useEffect, useState } from "react"
import { Controller, useForm, useWatch } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import {
  Autocomplete,
  Box,
  Card,
  CircularProgress,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import InfoIcon from "@mui/icons-material/Info"
import EditIcon from "@mui/icons-material/Edit"
import FormControl from "@mui/material/FormControl"
import FormLabel from "@mui/material/FormLabel"
import MDBox from "components/MDBox"
import MDInput from "components/MDInput"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"
import MDSnackbar from "components/MDSnackbar"
import ProductsSearchWithModal from "components/MDModal/ProductsSearchWithModal"
import DashboardLayout from "examples/LayoutContainers/DashboardLayout"
import DashboardNavbar from "examples/Navbars/DashboardNavbar"
import * as translate from "utils/translate"

import { translateAccountName } from "utils/translate/status"

// Service
import * as packageService from "../../../services/packages/package.service"
import * as companyService from "../../../services/company/company.service"
import * as productService from "../../../services/products/product.service"
import * as clientService from "../../../services/clients/client.service"
import * as packagesConstants from "../../../utils/constants/packages"

// Custom components
import AntSwitch from "./AntSwitch"
import { getStateMaylrezCode } from "utils/functions/global"
import ClientCreateModal from "components/MDModal/clientCreateModal"
import ClientDetailsModal from "components/MDModal/ClientDetailsModal"
import { CheckCircle, Delete } from "@mui/icons-material"

export default function PackageForm() {
  const params = useParams()
  const navigate = useNavigate()
  const [fullGovernorates, setFullGovernorates] = useState([])
  const [governorates, setGovernorates] = useState([])
  const [cities, setCities] = useState([])
  const [state, setState] = useState("-")
  const [city, setCity] = useState("-")
  const [companyId, setCompanyId] = useState(0)
  const [companyName, setCompanyName] = useState("")
  const [owner, setOwner] = useState("-")
  const [lazajellaType, setLazajellaType] = useState("FIX")
  const [isExchangeChecked, setIsExchangeChecked] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [snackbarObject, setSnackbarObject] = useState({
    open: false,
    title: "",
    content: "",
    color: "primary",
    icon: "",
  })
  const [companiesList, setCompaniesList] = useState([])
  const [products, setProducts] = useState([])
  const [isPackageCreateForm, setIsPackageCreateForm] = useState(false)
  const [isConfirmed, setIsConfirmed] = useState(false)
  const isChahinSelected =
    companiesList.find((companyItem) => companyItem.companyId === companyId)?.companyName?.toLowerCase() === "chahin"
  const { control, handleSubmit, reset, setValue } = useForm({
    defaultValues: {
      productQuantity: 1,
      unitPrice: 0,
      packagePrice: 0,
      deliveryFee: 0,
      selectedProducts: [],
    },
  })

  // console.log("getValues =>", getValues())
  const [clientsList, setClientsList] = useState([])
  const [selectedClient, setSelectedClient] = useState(null)
  const [isClientModalOpen, setIsClientModalOpen] = useState(false)
  const [isClientDetailsOpen, setIsClientDetailsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedProducts, setSelectedProducts] = useState([])
  const [originalItemsMap, setOriginalItemsMap] = useState(new Map())

  const deliveryFeeValue = useWatch({ control, name: "deliveryFee" }) || 0

  // ***************************************

  // get available variants
  const getAvailableVariants = (product) => {
    if (!product?.stocks?.length) return []

    const variantsMap = new Map()

    product.stocks.forEach((stock) => {
      stock.variants?.forEach((variant) => {
        const key = `${variant.color || ""}-${variant.size || ""}-${variant.gender || ""}`

        if (!variantsMap.has(key)) {
          variantsMap.set(key, {
            variantId: variant.id,
            color: variant.color,
            size: variant.size,
            gender: variant.gender,
            availableQuantity: 0,
            label: [variant.color, variant.size, variant.gender].filter(Boolean).join(" / ") || "Par défaut",
          })
        }

        // Sum available quantity from all depots
        const existing = variantsMap.get(key)
        existing.availableQuantity += variant.availableQuantity || 0
      })
    })

    return Array.from(variantsMap.values()).filter((variant) => variant.availableQuantity > 0)
  }

  //  handle add variant
  const handleAddVariant = (productIndex, color = null, size = null, gender = null, variantId = null) => {
    const updated = [...selectedProducts]
    const product = updated[productIndex]

    const labelParts = []
    if (color) labelParts.push(color)
    if (size) labelParts.push(size)
    if (gender) labelParts.push(gender)
    const label = labelParts.join(" / ") || "Par défaut"

    const existingVariant = product?.variants?.find((v) =>
      product.hasVariants ? v.variantId === variantId : v.variantId === null
    )

    if (existingVariant) {
      return
    }

    const basePrice = Number(product.isOnPromo ? product.promoPrice : product.salePrice) || 0

    const newVariant = {
      variantId: variantId,
      label: label,
      color: color,
      size: size,
      gender: gender,
      quantity: 1,
      unitPrice: basePrice,
      totalPrice: basePrice,
    }

    updated[productIndex].variants.push(newVariant)
    setSelectedProducts(updated)
  }

  //  handle product select
  const handleProductSelect = (newProducts) => {
    setSelectedProducts((prevSelectedProducts) => {
      const existingProductsMap = new Map()
      prevSelectedProducts.forEach((product) => {
        existingProductsMap.set(product.id, product)
      })

      const mergedProducts = newProducts.map((newProduct) => {
        const existingProduct = existingProductsMap.get(newProduct.id)

        // Calculate total available quantity
        const availableQuantity =
          newProduct?.stocks?.reduce((sum, stock) => sum + (stock.availableQuantity || 0), 0) || 0

        const hasVariants = newProduct.stocks?.some((stock) => stock.variants && stock.variants.length > 0) || false

        if (existingProduct) {
          return {
            ...newProduct,
            availableQuantity,
            hasVariants,
            variants: existingProduct.variants,
          }
        } else {
          const variants = hasVariants
            ? []
            : availableQuantity > 0
            ? [
                {
                  variantId: null,
                  label: "Par défaut",
                  color: null,
                  size: null,
                  gender: null,
                  quantity: 1,
                  unitPrice: Number(newProduct.isOnPromo ? newProduct.promoPrice : newProduct.salePrice) || 0,
                  totalPrice: Number(newProduct.isOnPromo ? newProduct.promoPrice : newProduct.salePrice) || 0,
                },
              ]
            : []

          return {
            ...newProduct,
            availableQuantity,
            hasVariants,
            variants,
          }
        }
      })

      return mergedProducts
    })
  }

  const handleDeliveryFeeChange = (e) => {
    const fee = parseFloat(e.target.value) || 0
    setValue("deliveryFee", fee)
  }

  const handleTotalChange = (e) => {
    const value = parseFloat(e.target.value) || 0
    setValue("packagePrice", value)
  }

  // search client by name, phone, email
  const searchClients = async (searchTerm) => {
    try {
      const { data } = await clientService.searchClients(searchTerm)
      setClientsList(data || [])
    } catch (error) {
      // console.error("Error searching clients:", error)
      setClientsList([])
    }
  }

  const handleClientSelect = (client) => {
    if (!client) return
    // openClientDetailsModal(client)
    setSelectedClient(client)

    // console.log("client", client)
    setValue("clientId", client.clientId)
    setValue("clientName", client.fullName)
    setValue("clientPhoneNumber", client.phoneNumber || "")
    setValue("address", client.address || "")
    setValue("state", client.state || "")

    setState(client.state || "")

    const citiesPerGovernorate = fullGovernorates.filter((g) => g.gouvernorat === client.state)
    const mappingCities = citiesPerGovernorate.map((g) => g.villes)
    setCities(mappingCities[0] || [])

    setValue("city", client.city || "")
    setCity(client.city || "")
  }

  // create/edit client
  const handleClientModalSave = async (clientData, clientId = null) => {
    try {
      const response = clientId
        ? await clientService.updateClient(clientId, clientData)
        : await clientService.createClient(clientData)

      const { data, status } = response || {}

      const client = data?.data || null

      switch (status) {
        case 200:
        case 201:
          handleOpenSnackbar("success", clientId ? "Client mis à jour" : "Client créé", data.message, "check_circle")
          if (client) {
            handleClientSelect(client)
            setClientsList((prev) => [...prev, client])
          }
          closeClientModal()
          break

        case 303:
          handleOpenSnackbar("info", "Client existant", data.message, "info")
          break

        case 400:
        case 422:
          handleOpenSnackbar("warning", "Validation", data.message, "warning")
          break

        case 500:
          handleOpenSnackbar("error", "Erreur serveur", data.message, "error")
          break

        default:
          handleOpenSnackbar("info", "Information", data.message, "info")
      }
    } catch (error) {
      handleOpenSnackbar("error", "Erreur client", error.message || "Une erreur est survenue", "error")
    }
  }

  // delete client
  // const handleDeleteClient = async (client) => {
  //   if (!client) return

  //   const confirmDelete = window.confirm(`Voulez-vous vraiment supprimer le client "${client.fullName}" ?`)
  //   if (!confirmDelete) return

  //   try {
  //     const response = await clientService.deleteClient(client.clientId)

  //     if (response?.status === 200) {
  //       handleOpenSnackbar(
  //         "success",
  //         "Client supprimé",
  //         `Le client "${client.fullName}" a été supprimé avec succès`,
  //         "check_circle"
  //       )

  //       // Remove client from local state
  //       setClientsList((prev) => prev.filter((c) => c.clientId !== client.clientId))

  //       // Clear selected client if it was the deleted one
  //       if (selectedClient?.clientId === client.clientId) {
  //         setSelectedClient(null)
  //         setValue("clientId", "")
  //       }
  //     } else {
  //       handleOpenSnackbar("error", "Erreur", response?.message || "Impossible de supprimer le client", "error")
  //     }
  //   } catch (error) {
  //     handleOpenSnackbar("error", "Erreur", error.message || "Une erreur est survenue", "error")
  //   }
  // }

  const openClientModal = (client = null) => {
    setSelectedClient(client)
    setIsClientModalOpen(true)
  }

  const closeClientModal = () => {
    setIsClientModalOpen(false)
  }

  const openClientDetailsModal = (client) => {
    setSelectedClient(client)
    setIsClientDetailsOpen(true)
  }

  const closeClientDetailsModal = () => {
    setIsClientDetailsOpen(false)
  }

  // ***************************************

  // Handle unit price change for a variant
  const handleVariantUnitPriceChange = (productIndex, variantIndex, value) => {
    const updated = [...selectedProducts]
    const price = parseFloat(value) || 0
    updated[productIndex].variants[variantIndex].unitPrice = price
    updated[productIndex].variants[variantIndex].totalPrice =
      price * updated[productIndex].variants[variantIndex].quantity
    setSelectedProducts(updated)
  }

  // Handle quantity change for a variant
  const handleVariantQuantityChange = (productIndex, variantIndex, value, maxQuantity = null) => {
    const updated = [...selectedProducts]
    let qty = Math.max(1, parseInt(value) || 1)

    if (maxQuantity !== null && maxQuantity > 0) {
      qty = Math.min(qty, maxQuantity)
    }

    updated[productIndex].variants[variantIndex].quantity = qty
    updated[productIndex].variants[variantIndex].totalPrice =
      updated[productIndex].variants[variantIndex].unitPrice * qty
    setSelectedProducts(updated)
  }

  // Handle reset form
  const handleResetForm = () => {
    const redirectPath = isConfirmed ? "/colis" : "/pre-selection-commandes"
    // console.log("redirectPath =>", redirectPath)
    navigate(redirectPath)

    reset()
    setCompanyId(0)
    setOwner("-")
    setLazajellaType("fix")
    setIsExchangeChecked(false)
    setState("-")
    setCity("-")
    setCompanyName("")
  }

  // Handle lazajella type event
  const handleChangeLazajellaType = (evt) => {
    setLazajellaType(evt.target.value)
  }

  // Handle owner event
  const handleChangeOwner = (evt) => {
    const selectedOwnerKey = evt.target.value
    setOwner(selectedOwnerKey)

    setSelectedProducts([])
    setProducts([])
    setValue("selectedProducts", [])

    if (selectedOwnerKey && selectedOwnerKey !== "-") {
      findAllProducts(selectedOwnerKey)
    }
  }

  // Handle company event
  const handleChangeCompany = (evt) => {
    const selectedCompanyKey = evt.target.value
    setCompanyId(selectedCompanyKey)

    setSelectedProducts([])
    setProducts([])
    setValue("selectedProducts", [])
  }

  // Handle exchange event
  const handleChangeExchange = (evt) => {
    setIsExchangeChecked(evt.target.checked)
  }

  // Handle open snackbar
  const handleOpenSnackbar = (status, title, content, icon) => {
    setSnackbarObject({
      ...snackbarObject,
      title,
      open: true,
      content,
      color: status,
      icon,
    })
  }

  // Handle close snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return
    }
    setSnackbarObject({
      ...snackbarObject,
      open: false,
    })
  }

  // Variant validation function
  const validateSelectedProducts = () => {
    if (selectedProducts.length === 0) {
      return "Veuillez sélectionner au moins un produit."
    }

    for (const product of selectedProducts) {
      // For products with variants, at least one variant must be selected
      if (product.hasVariants && product.variants.length === 0) {
        return `Le produit "${product.name}" nécessite au moins une variante sélectionnée.`
      }

      // For simple products without variants, ensure the variant is added
      if (!product.hasVariants && product.variants.length === 0) {
        return `Le produit "${product.name}" doit être ajouté.`
      }

      // Validate quantities don't exceed available stock
      for (const variant of product.variants) {
        if (variant.quantity < 1) {
          return `Toutes les variantes doivent avoir une quantité d'au moins 1.`
        }

        // Get available quantity for validation
        let availableQuantity = 0
        if (product.hasVariants) {
          const availableVariant = getAvailableVariants(product).find((v) => v.variantId === variant.variantId)
          availableQuantity = availableVariant?.availableQuantity || 0
        } else {
          availableQuantity = product.availableQuantity || 0
        }

        // When editing an existing package, only validate the increase in quantity
        // (the original quantity is already consumed in stock, so only the delta matters)
        const itemKey = `${product.id}_${String(variant.variantId)}`
        const originalQty = originalItemsMap.get(itemKey) || 0
        const additionalQtyNeeded = variant.quantity - originalQty

        if (additionalQtyNeeded > availableQuantity) {
          return `La quantité pour "${product.name} - ${variant.label}" dépasse le stock disponible (${availableQuantity}).`
        }
      }
    }
    return null
  }

  // prepare Items for API
  const prepareItemsForAPI = (selectedProducts) => {
    return selectedProducts.flatMap((product) =>
      product.variants.map((variant) => ({
        productId: product.id,
        variantId: variant.variantId,
        quantity: variant.quantity,
        unitPrice: variant.unitPrice,
        totalPrice: variant.totalPrice,
      }))
    )
  }

  // prepare payload for API
  const preparePackageData = (body, selectedProducts, selectedClient) => {
    const productNames = selectedProducts?.map((p) => p.name).join(", ")
    // const description = `${productNames} - ${body.description}`
    const description = `${productNames}`
    const items = prepareItemsForAPI(selectedProducts)

    return {
      ...body,
      companyId,
      exchange: isExchangeChecked,
      state: state,
      city,
      lazajellaType,
      owner,
      description,
      deliveryFee: body.deliveryFee || 0,
      clientId: selectedClient?.clientId || null,
      items,
      productQuantity: 1,
    }
  }

  // Handle create new package (api call)
  const createNewPackage = async (body) => {
    setIsLoading(true)
    try {
      const validationError = validateSelectedProducts()
      if (validationError) {
        handleOpenSnackbar("error", "Validation", validationError, "error")
        setIsLoading(false)
        return
      }

      // Prepare filtered data for API
      const payload = preparePackageData(body, selectedProducts, selectedClient)

      // Remove unnecessary fields
      delete payload.selectedProducts
      delete payload.selectedVariantId
      delete payload.unitPrice

      // console.log("payload =>", payload)
      // return
      // payload.productQuantity = 1

      // Create the package
      const { status } = await packageService.createPackage(payload)

      if (status === 201) {
        handleOpenSnackbar("success", translate.new_package, "Le nouveau colis a été créé avec succès", "check_circle")

        // Reset the form
        handleResetForm()
        setTimeout(() => navigate("/pre-selection-commandes"), 1000)
      }

      setIsLoading(false)
    } catch (error) {
      if (error?.message === "Error: AxiosError: Request failed with status code 409") {
        handleOpenSnackbar("error", translate.new_package, "Ce colis déjà creé", "error")
        setIsLoading(false)
        return
      }
      handleOpenSnackbar("error", translate.new_package, translate.something_wrong, "error")
      setIsLoading(false)
    }
  }

  // Update the package (api call)
  const updatePackage = async (body) => {
    setIsLoading(true)
    try {
      const validationError = validateSelectedProducts()
      if (validationError) {
        handleOpenSnackbar("error", "Validation", validationError, "error")
        setIsLoading(false)
        return
      }

      // Prepare filtered data for API
      const payload = preparePackageData(body, selectedProducts, selectedClient)

      // Remove unnecessary fields
      delete payload.selectedProducts
      delete payload.selectedVariantId
      delete payload.unitPrice

      // console.log("payload =>", payload)
      const response = await packageService.updatePackage(params.packageId, payload)
      const { status, message, carrierWarning } = response.data

      if (status === 200) {
        if (carrierWarning) {
          handleOpenSnackbar("warning", translate.update_package, carrierWarning, "warning")
        } else {
          handleOpenSnackbar("success", translate.update_package, "Le colis a été modifié avec succès", "check_circle")
        }
        // Reset the form
        handleResetForm()
        const redirectPath = isConfirmed ? "/colis" : "/pre-selection-commandes"
        setTimeout(() => navigate(redirectPath), 3000)
      } else {
        const msg = message || "Impossible de modifier le colis"
        handleOpenSnackbar("error", msg, "error")
      }

      setIsLoading(false)
    } catch (error) {
      const message = error?.response?.data?.message || translate.something_wrong || "Une erreur est survenue"
      handleOpenSnackbar("error", message, "error")
      setIsLoading(false)
    }
  }

  // Find all companies (api call)
  const findAllCompanies = async () => {
    try {
      const { data } = await companyService.findAllCompanies()
      setCompaniesList(data)
      return data || []
    } catch (error) {
      return error
    }
  }

  // Find all governorates (api call)
  const findAllGovernorates = async () => {
    const { packageId } = params
    try {
      const data = packagesConstants.governoratesData
      setFullGovernorates(data)
      const mappingGovernorates = data.map((governorateItem) => governorateItem.gouvernorat)
      const mappingCities = data.map((governorateItem) => governorateItem.villes)
      setGovernorates(mappingGovernorates)
      if (packageId !== undefined) setCities(mappingCities.flat())
    } catch (error) {
      setFullGovernorates([])
      setGovernorates([])
    }
  }

  //  find all products (api call)
  const findAllProducts = async (ownerKey) => {
    try {
      const params = {}

      if (ownerKey && ownerKey !== "-") {
        params.subCompanyName = translateAccountName(ownerKey)
      }

      const { data } = await productService.searchAllProducts(params)
      // console.log("data =>", data)
      setProducts(data || [])
      return data || []
    } catch (error) {
      setProducts([])
      return []
    }
  }

  // Find one package
  // ? This is temporary method until it will change with state management store
  const findOnePackageById = async () => {
    const { packageId } = params
    if (packageId === undefined) return setIsPackageCreateForm(true)
    try {
      const { data } = await packageService.findOnePackage(packageId)
      // console.log("data =>", data)
      const packageObject = data[0]
      setValue("clientName", packageObject?.clientName)
      setValue("packagePrice", packageObject?.packagePrice)
      setValue("description", packageObject?.description)
      setValue("productQuantity", packageObject?.productQuantity)
      setValue("comments", packageObject?.comments)
      setValue("clientPhoneNumber", packageObject?.clientPhoneNumber)
      setValue("address", packageObject?.address)
      setValue("deliveryFee", packageObject?.deliveryFee)

      setCompanyId(packageObject?.companyId)
      setIsExchangeChecked(Boolean(packageObject?.exchange))
      setOwner(packageObject?.owner)
      setLazajellaType(packageObject?.lazajellaType)
      setState(packageObject?.state)
      setCity(packageObject?.city)
      setCompanyName(packageObject?.companyName)
      setIsConfirmed(packageObject?.isConfirmed || false)

      if (packageObject?.clientPhoneNumber) {
        try {
          const { data } = await clientService.searchClients(packageObject.clientPhoneNumber)

          if (data && data.length > 0) {
            const matchedClient = data[0]
            setSelectedClient(matchedClient)
            setValue("clientId", matchedClient.clientId)
          }
        } catch (error) {
          // console.warn("Error :", error)
        }
      }

      if (data?.items && data?.items?.length > 0) {
        await handlePackageItems(data.items, packageObject.owner)
      } else if (packageObject?.productId) {
        await handleLegacyPackage(packageObject)
      }

      return packageObject
    } catch (error) {
      // console.log("error=>", error)
      handleOpenSnackbar("error", translate.update_package, translate.something_wrong, "error")
      return setIsPackageCreateForm(true)
    }
  }

  // Helper function to handle new packages with items array
  const handlePackageItems = async (items, owner) => {
    const productList = await findAllProducts(owner)

    const itemsByProduct = items.reduce((acc, item) => {
      if (!acc[item.productId]) {
        acc[item.productId] = []
      }
      acc[item.productId].push(item)
      return acc
    }, {})

    const formattedProducts = Object.entries(itemsByProduct).map(([productId, productItems]) => {
      const product = productList.find((p) => p.id === parseInt(productId))

      if (!product) {
        return createProductFromItems(productItems)
      }

      return createProductWithMultipleVariants(product, productItems)
    })

    setSelectedProducts(formattedProducts)
    setValue("selectedProducts", formattedProducts)
    setProducts(productList)

    // Store original quantities to enable delta-based stock validation on edit
    const map = new Map()
    items.forEach((item) => {
      const key = `${item.productId}_${String(item.variantId)}`
      map.set(key, item.quantity)
    })
    setOriginalItemsMap(map)
  }

  // Helper function for legacy packages (single product)
  const handleLegacyPackage = async (packageObject) => {
    const productList = await findAllProducts(packageObject.owner)
    const selectedProduct = productList.find((p) => p.id === packageObject.productId)

    if (selectedProduct) {
      const allVariants = selectedProduct.stocks?.flatMap((s) => s.variants || []) || []
      const selectedVariant = allVariants.find((v) => v.id === packageObject.variantId) || null

      const productData = {
        ...selectedProduct,
        variants: selectedVariant
          ? [
              {
                variantId: selectedVariant.id,
                label: [selectedVariant.color, selectedVariant.size, selectedVariant.gender]
                  .filter(Boolean)
                  .join(" / "),
                color: selectedVariant.color,
                size: selectedVariant.size,
                gender: selectedVariant.gender,
                quantity: packageObject.productQuantity,
                unitPrice:
                  packageObject.unitPrice ||
                  Number(selectedProduct.isOnPromo ? selectedProduct.promoPrice : selectedProduct.salePrice),
                totalPrice:
                  (packageObject.unitPrice ||
                    Number(selectedProduct.isOnPromo ? selectedProduct.promoPrice : selectedProduct.salePrice)) *
                  (packageObject.productQuantity || 1),
              },
            ]
          : [
              {
                variantId: null,
                label: "Default",
                quantity: packageObject.productQuantity || 1,
                unitPrice:
                  packageObject.unitPrice ||
                  Number(selectedProduct.isOnPromo ? selectedProduct.promoPrice : selectedProduct.salePrice),
                totalPrice:
                  (packageObject.unitPrice ||
                    Number(selectedProduct.isOnPromo ? selectedProduct.promoPrice : selectedProduct.salePrice)) *
                  (packageObject.productQuantity || 1),
              },
            ],
        selectedColor: selectedVariant?.color || null,
        selectedSize: selectedVariant?.size || null,
        selectedGender: selectedVariant?.gender || null,
      }

      setSelectedProducts([productData])
      setValue("selectedProducts", [productData])
      setProducts([selectedProduct])

      // Store original quantity for delta-based stock validation on edit
      const key = `${packageObject.productId}_${String(packageObject.variantId ?? null)}`
      setOriginalItemsMap(new Map([[key, packageObject.productQuantity || 1]]))
    }
  }

  // Create product object when product is not found in the list (with multiple variants)
  const createProductFromItems = (items) => {
    const firstItem = items[0]

    return {
      id: firstItem.productId,
      name: `Product ${firstItem.productId}`,
      reference: "N/A",
      salePrice: firstItem.unitPrice,
      promoPrice: firstItem.unitPrice,
      isOnPromo: false,
      availableQuantity: 0,
      hasVariants: items.some((item) => item.variantId),
      variants: items.map((item) => ({
        variantId: item.variantId,
        label: item.variantId ? `Variante ${item.variantId}` : "Par défaut",
        color: null,
        size: null,
        gender: null,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        totalPrice: item.totalPrice,
      })),
      selectedColor: null,
      selectedSize: null,
      selectedGender: null,
    }
  }

  // Create proper product structure with multiple variants
  const createProductWithMultipleVariants = (product, items) => {
    const hasVariants = product.stocks?.some((stock) => stock.variants && stock.variants.length > 0) || false

    const variants = items.map((item) => {
      let variantDetails = {}

      if (item?.variantId) {
        const allVariants = product.stocks?.flatMap((s) => s.variants || []) || []
        const selectedVariant = allVariants.find((v) => v.id === item.variantId)

        if (selectedVariant) {
          variantDetails = {
            color: selectedVariant.color,
            size: selectedVariant.size,
            gender: selectedVariant.gender,
            label:
              [selectedVariant.color, selectedVariant.size, selectedVariant.gender].filter(Boolean).join(" / ") ||
              "Variante",
          }
        }
      }

      return {
        variantId: item.variantId,
        label: variantDetails.label || (item.variantId ? `Variante ${item.variantId}` : "Par défaut"),
        color: variantDetails.color,
        size: variantDetails.size,
        gender: variantDetails.gender,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        totalPrice: item.totalPrice,
      }
    })

    return {
      ...product,
      availableQuantity: product.stocks?.reduce((sum, stock) => sum + (stock.availableQuantity || 0), 0) || 0,
      hasVariants,
      variants,
      selectedColor: null,
      selectedSize: null,
      selectedGender: null,
    }
  }

  // Handle state event
  const handleChangeState = (evt) => {
    // console.log("evt.target.value ====>", evt.target.value)
    setState(evt.target.value)
    const citiesPerGovernorate = fullGovernorates.filter(
      (governorateItem) => governorateItem.gouvernorat === evt.target.value
    )
    const mappingCities = citiesPerGovernorate.map((citiesPerGovernorateItem) => citiesPerGovernorateItem.villes)
    setCities(mappingCities[0])
  }

  // Handle city event
  const handleChangeCity = (evt) => {
    setCity(evt.target.value)
  }

  // Handle delete a variant
  const handleDeleteVariant = (productIndex, variantIndex) => {
    const updated = [...selectedProducts]
    updated[productIndex].variants.splice(variantIndex, 1)
    setSelectedProducts(updated)
  }

  useEffect(() => {
    if (!searchTerm) {
      setClientsList([])
      return
    }

    const delayDebounce = setTimeout(() => {
      searchClients(searchTerm)
    }, 300)

    return () => clearTimeout(delayDebounce)
  }, [searchTerm])

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)
      try {
        await findAllGovernorates()
        await findAllCompanies()

        const packageObject = await findOnePackageById()

        if (packageObject?.productId) {
          const productList = await findAllProducts(packageObject.owner)
          const selectedProduct = productList.find((p) => p.id === packageObject.productId)

          if (selectedProduct) {
            const allVariants = selectedProduct.stocks?.flatMap((s) => s.variants || []) || []
            const selectedVariant = allVariants.find((v) => v.id === packageObject.variantId) || null

            const productData = {
              ...selectedProduct,
              variants: selectedVariant
                ? [
                    {
                      variantId: selectedVariant.id,
                      label: [selectedVariant.color, selectedVariant.size, selectedVariant.gender]
                        .filter(Boolean)
                        .join(" / "),
                      color: selectedVariant.color,
                      size: selectedVariant.size,
                      gender: selectedVariant.gender,
                      quantity: packageObject.productQuantity,
                      unitPrice:
                        packageObject.unitPrice ||
                        Number(selectedProduct.isOnPromo ? selectedProduct.promoPrice : selectedProduct.salePrice),
                      totalPrice:
                        (packageObject.unitPrice ||
                          Number(selectedProduct.isOnPromo ? selectedProduct.promoPrice : selectedProduct.salePrice)) *
                        (packageObject.productQuantity || 1),
                    },
                  ]
                : [
                    {
                      variantId: null,
                      label: "Default",
                      quantity: packageObject.productQuantity || 1,
                      unitPrice:
                        packageObject.unitPrice ||
                        Number(selectedProduct.isOnPromo ? selectedProduct.promoPrice : selectedProduct.salePrice),
                      totalPrice:
                        (packageObject.unitPrice ||
                          Number(selectedProduct.isOnPromo ? selectedProduct.promoPrice : selectedProduct.salePrice)) *
                        (packageObject.productQuantity || 1),
                    },
                  ],
              selectedColor: selectedVariant?.color || null,
              selectedSize: selectedVariant?.size || null,
              selectedGender: selectedVariant?.gender || null,
            }

            setSelectedProducts([productData])
            setValue("selectedProducts", [productData])
            setProducts([selectedProduct])

            // Store original quantity for delta-based stock validation on edit
            const key = `${packageObject.productId}_${String(packageObject.variantId ?? null)}`
            setOriginalItemsMap(new Map([[key, packageObject.productQuantity || 1]]))
          }
        }
      } catch (err) {
        // console.error("Error loading package form data:", err)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const itemsTotal = selectedProducts.reduce((total, product) => {
      const productTotal = product.variants.reduce((sum, variant) => sum + variant.totalPrice, 0)
      return total + productTotal
    }, 0)

    const finalTotal = itemsTotal + deliveryFeeValue
    setValue("packagePrice", finalTotal)
  }, [selectedProducts, deliveryFeeValue, setValue])

  return (
    <>
      {isLoading ? (
        <>
          <DashboardLayout>
            <DashboardNavbar absolute isMini />
            <MDBox display="flex" justifyContent="center" alignItems="center" height="80vh">
              <CircularProgress size={60} />
            </MDBox>
          </DashboardLayout>
        </>
      ) : (
        <>
          <DashboardLayout>
            <DashboardNavbar absolute isMini />
            <MDBox pt={6} pb={3}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card sx={{ height: "100%" }}>
                    <MDBox px={2} py={2} display="flex" justifyContent="space-between" alignItems="center">
                      <MDTypography variant="h6" fontWeight="medium">
                        {isPackageCreateForm ? "Créer un nouveau colis" : "Mettre à jour le colis"}
                      </MDTypography>
                    </MDBox>
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Card sx={{ height: "100%" }}>
                    <MDBox
                      component="form"
                      px={2}
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      onSubmit={handleSubmit(isPackageCreateForm ? createNewPackage : updatePackage)}
                      noValidate
                      autoComplete="off"
                    >
                      {/* company id */}
                      <Controller
                        control={control}
                        name="companyId"
                        rules={{
                          validate: () => companyId > 0 || "Sélectionnez une entreprise",
                        }}
                        render={({ field: { ref, onChange, value, ...field }, fieldState: { error } }) => (
                          <FormControl
                            fullWidth
                            margin="normal"
                            variant="standard"
                            sx={{ pt: 1, minWidth: 120 }}
                            error={Boolean(error) && companyId === 0}
                            disabled={!isPackageCreateForm && isConfirmed}
                          >
                            <InputLabel id="id-company_id-label">Entreprise</InputLabel>
                            <Select
                              labelId="id-company_id-label"
                              id="id-company_id"
                              value={companyId}
                              onChange={handleChangeCompany}
                              label="Entreprise"
                              size="medium"
                              {...field}
                            >
                              <MenuItem value={0}>
                                <em>Aucune</em>
                              </MenuItem>
                              {isPackageCreateForm || (!isPackageCreateForm && !isConfirmed) ? (
                                companiesList.map((companyItem) => (
                                  <MenuItem
                                    key={companyItem.companyId}
                                    value={companyItem.companyId}
                                    data-company_name="Company name"
                                  >
                                    {companyItem?.companyName}
                                  </MenuItem>
                                ))
                              ) : (
                                <MenuItem value={companyId} data-company_name="Company name">
                                  {companyName}
                                </MenuItem>
                              )}
                            </Select>
                            {Boolean(error) && companyId === 0 && <FormHelperText>{error?.message}</FormHelperText>}
                          </FormControl>
                        )}
                      />

                      {companyId === 1 && (
                        <Controller
                          control={control}
                          name="lazajellaType"
                          rules={{
                            validate: () => lazajellaType !== "-" || "Sélectionner le type de lazajella",
                          }}
                          render={({ field: { ref, onChange, value, ...field }, fieldState: { error } }) => (
                            <FormControl
                              fullWidth
                              margin="normal"
                              variant="standard"
                              sx={{ pt: 1, minWidth: 120 }}
                              error={Boolean(error) && lazajellaType === "-"}
                            >
                              <InputLabel id="id-lazajellaType-label">Type</InputLabel>
                              <Select
                                labelId="id-lazajellaType-label"
                                id="id-lazajellaType"
                                value={lazajellaType}
                                onChange={handleChangeLazajellaType}
                                label="LazajellaType"
                                size="medium"
                                {...field}
                              >
                                <MenuItem value="-" disabled>
                                  <em>Aucune</em>
                                </MenuItem>
                                <MenuItem value="FIX">FIX</MenuItem>
                                <MenuItem value="ONP">ONP</MenuItem>
                                <MenuItem value="BLK">BLK</MenuItem>
                                <MenuItem value="SMD">SMD</MenuItem>
                              </Select>
                              {Boolean(error) && lazajellaType === "-" && (
                                <FormHelperText>{error?.message}</FormHelperText>
                              )}
                            </FormControl>
                          )}
                        />
                      )}

                      {/* owner */}
                      <Controller
                        control={control}
                        name="owner"
                        defaultValue=""
                        rules={{
                          validate: () => owner !== "-" || "Sélectionner un compte",
                        }}
                        render={({ field: { ref, onChange, value, ...field }, fieldState: { error } }) => (
                          <FormControl
                            fullWidth
                            margin="normal"
                            variant="standard"
                            sx={{ pt: 1, minWidth: 120 }}
                            error={Boolean(error) && owner === "-"}
                            disabled={!isPackageCreateForm && isConfirmed}
                          >
                            <InputLabel id="id-owner-label">Compte</InputLabel>
                            {isPackageCreateForm || (!isPackageCreateForm && !isConfirmed) ? (
                              <Select
                                labelId="id-owner-label"
                                id="id-owner"
                                value={owner}
                                onChange={handleChangeOwner}
                                label="Owner"
                                size="medium"
                                {...field}
                              >
                                <MenuItem value="-" disabled>
                                  <em>Aucune</em>
                                </MenuItem>
                                <MenuItem value="account1">{translateAccountName("account1")}</MenuItem>
                                {(companyId === 1 || isChahinSelected) && (
                                  <MenuItem value="account2">{translateAccountName("account2")}</MenuItem>
                                )}
                                <MenuItem value="account3">{translateAccountName("account3")}</MenuItem>
                                <MenuItem value="account4">{translateAccountName("account4")}</MenuItem>
                              </Select>
                            ) : (
                              <Select
                                labelId="id-owner-label"
                                id="id-owner"
                                value={owner}
                                onChange={handleChangeOwner}
                                label="Owner"
                                size="medium"
                                {...field}
                              >
                                <MenuItem value="-" disabled>
                                  <em>Aucune</em>
                                </MenuItem>
                                <MenuItem value={owner}>{translateAccountName(owner)}</MenuItem>
                              </Select>
                            )}
                            {Boolean(error) && owner === "-" && <FormHelperText>{error?.message}</FormHelperText>}
                          </FormControl>
                        )}
                      />

                      {/* product */}
                      <Controller
                        name="selectedProducts"
                        control={control}
                        defaultValue={[]}
                        rules={{
                          required: "Veuillez sélectionner un produit.",
                          validate: (value) =>
                            (value && value.length > 0) || "Veuillez sélectionner au moins un produit.",
                        }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <>
                            <ProductsSearchWithModal
                              products={products}
                              onSelectProducts={(newProducts) => {
                                handleProductSelect(newProducts)
                                onChange(newProducts)
                              }}
                              selectedProductsProp={selectedProducts}
                              error={Boolean(error)}
                              helperText={
                                error
                                  ? error.message
                                  : owner === "-" || owner === ""
                                  ? "Sélectionnez d'abord un compte pour choisir des produits"
                                  : ""
                              }
                              disabled={owner === "-" || owner === ""}
                            />
                          </>
                        )}
                      />

                      {selectedProducts?.length > 0 && (
                        <Box mt={1} mb={2}>
                          <Typography variant="body2" color="primary">
                            {selectedProducts.length} produit(s) sélectionné(s)
                          </Typography>
                        </Box>
                      )}

                      {selectedProducts?.map((product, pIndex) => (
                        <Box
                          key={product?.id}
                          mb={3}
                          p={2}
                          sx={{ border: "1px solid #e0e0e0", borderRadius: 2, width: "100%" }}
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                            <Box flex={1}>
                              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                                {product?.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" display="block">
                                Réf: {product?.reference} | Prix:{" "}
                                {Number(product?.isOnPromo ? product?.promoPrice : product?.salePrice)?.toFixed(3)} DT
                                {product?.isOnPromo && " (Promo)"}
                              </Typography>
                              {!product?.hasVariants && (
                                <Typography variant="caption" color="primary" sx={{ mt: 0.5 }} display="block">
                                  Stock: {product?.availableQuantity || 0}
                                </Typography>
                              )}
                            </Box>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => {
                                const updated = selectedProducts.filter((_, index) => index !== pIndex)
                                setSelectedProducts(updated)
                              }}
                              // disabled={!isPackageCreateForm && isConfirmed}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>

                          {product?.hasVariants && (
                            <Box mb={2}>
                              <Typography variant="subtitle2" gutterBottom fontWeight="600" color="textSecondary">
                                Sélectionner une variante
                              </Typography>

                              {getAvailableVariants(product)?.length === 0 ? (
                                <Typography variant="caption" color="textSecondary" sx={{ fontStyle: "italic" }}>
                                  Aucune variante en stock
                                </Typography>
                              ) : (
                                <Autocomplete
                                  options={getAvailableVariants(product)}
                                  getOptionLabel={(option) => `${option?.label} (Stock: ${option?.availableQuantity})`}
                                  renderOption={(props, option) => {
                                    const isVariantAdded = product?.variants?.some(
                                      (v) => v.variantId === option?.variantId
                                    )
                                    return (
                                      <li
                                        {...props}
                                        style={{
                                          backgroundColor: isVariantAdded ? "rgba(76, 175, 80, 0.1)" : "transparent",
                                          opacity: option?.availableQuantity === 0 ? 0.5 : 1,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            width: "100%",
                                            alignItems: "center",
                                          }}
                                        >
                                          <Box>
                                            <Typography variant="body2">{option.label}</Typography>
                                            <Typography variant="caption" color="textSecondary">
                                              Stock: {option?.availableQuantity}
                                            </Typography>
                                          </Box>
                                          {isVariantAdded && <CheckCircle fontSize="small" color="success" />}
                                        </Box>
                                      </li>
                                    )
                                  }}
                                  filterOptions={(options, { inputValue }) => {
                                    return options?.filter((option) =>
                                      option?.label?.toLowerCase().includes(inputValue?.toLowerCase())
                                    )
                                  }}
                                  onChange={(event, selectedVariant) => {
                                    if (selectedVariant) {
                                      const isVariantAdded = product?.variants?.some(
                                        (v) => v.variantId === selectedVariant?.variantId
                                      )
                                      if (!isVariantAdded && selectedVariant?.availableQuantity > 0) {
                                        handleAddVariant(
                                          pIndex,
                                          selectedVariant?.color,
                                          selectedVariant?.size,
                                          selectedVariant?.gender,
                                          selectedVariant?.variantId
                                        )
                                      }
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      size="small"
                                      placeholder="Rechercher une variante..."
                                      variant="outlined"
                                    />
                                  )}
                                  disabled={getAvailableVariants(product).length === 0}
                                  sx={{ mb: 1 }}
                                />
                              )}
                            </Box>
                          )}

                          {product?.variants?.length > 0 && (
                            <Box mt={2}>
                              <Typography variant="subtitle2" gutterBottom fontWeight="600" color="textSecondary">
                                {product?.hasVariants
                                  ? `Variantes sélectionnées (${product?.variants?.length})`
                                  : "Produit sélectionné"}
                              </Typography>
                              <Box display="flex" flexDirection="column" gap={1}>
                                {product?.variants?.map((variant, vIndex) => {
                                  const availableVariant = product?.hasVariants
                                    ? getAvailableVariants(product).find((v) => v?.variantId === variant?.variantId)
                                    : { availableQuantity: product?.availableQuantity || 0 }

                                  const maxQuantity =
                                    availableVariant?.availableQuantity || product?.availableQuantity || 0

                                  return (
                                    <Paper
                                      key={vIndex}
                                      sx={{
                                        p: 1.5,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        borderRadius: 1,
                                        backgroundColor: "grey.50",
                                      }}
                                    >
                                      <Box flex={1}>
                                        <Typography variant="body2" fontWeight="500">
                                          {variant?.label}
                                        </Typography>
                                        {maxQuantity > 0 && (
                                          <Typography variant="caption" color="textSecondary">
                                            Stock: {maxQuantity}
                                          </Typography>
                                        )}
                                      </Box>

                                      <Box display="flex" alignItems="center" gap={1.5}>
                                        <TextField
                                          label="Prix"
                                          type="number"
                                          size="small"
                                          value={variant?.unitPrice}
                                          onChange={(e) => handleVariantUnitPriceChange(pIndex, vIndex, e.target.value)}
                                          inputProps={{
                                            min: 0,
                                            step: 0.01,
                                            style: { fontSize: "0.875rem" },
                                          }}
                                          sx={{ width: 90 }}
                                          variant="outlined"
                                          // disabled={!isPackageCreateForm && isConfirmed}
                                        />

                                        <TextField
                                          label="Qté"
                                          type="number"
                                          size="small"
                                          value={variant?.quantity}
                                          onChange={(e) =>
                                            handleVariantQuantityChange(pIndex, vIndex, e.target.value, maxQuantity)
                                          }
                                          inputProps={{
                                            min: 1,
                                            max: maxQuantity,
                                            style: { fontSize: "0.875rem" },
                                          }}
                                          error={variant?.quantity > maxQuantity}
                                          helperText={variant?.quantity > maxQuantity ? `Max: ${maxQuantity}` : ""}
                                          sx={{ width: 80 }}
                                          variant="outlined"
                                          // disabled={!isPackageCreateForm && isConfirmed}
                                        />

                                        <Typography variant="body2" fontWeight="600" sx={{ minWidth: 80 }}>
                                          {variant?.totalPrice?.toFixed(3)} DT
                                        </Typography>

                                        {product?.hasVariants && (
                                          <IconButton
                                            color="error"
                                            size="small"
                                            onClick={() => handleDeleteVariant(pIndex, vIndex)}
                                            // disabled={!isPackageCreateForm && isConfirmed}
                                          >
                                            <Delete fontSize="small" />
                                          </IconButton>
                                        )}
                                      </Box>
                                    </Paper>
                                  )
                                })}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      ))}

                      {/* Delivery Fee */}
                      <Controller
                        control={control}
                        name="deliveryFee"
                        defaultValue={0}
                        render={({ field }) => (
                          <MDInput
                            fullWidth
                            variant="standard"
                            margin="normal"
                            type="number"
                            label="Frais de livraison (TND)"
                            value={field.value}
                            onChange={(e) => {
                              field.onChange(e)
                              handleDeliveryFeeChange(e)
                            }}
                            inputProps={{ min: 0 }}
                            disabled={selectedProducts?.length === 0}
                          />
                        )}
                      />

                      {/* Total Price */}
                      <Controller
                        control={control}
                        name="packagePrice"
                        defaultValue={0}
                        render={({ field }) => (
                          <MDInput
                            fullWidth
                            variant="standard"
                            margin="normal"
                            type="number"
                            label="Prix total (TND)"
                            value={field.value}
                            onChange={(e) => {
                              field.onChange(e)
                              handleTotalChange(e)
                            }}
                            inputProps={{ min: 0 }}
                            disabled={selectedProducts?.length === 0}
                          />
                        )}
                      />

                      {/* client */}
                      <Controller
                        name="clientId"
                        control={control}
                        defaultValue={selectedClient?.clientId || ""}
                        rules={{ required: { value: true, message: "Le client est requis" } }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <Autocomplete
                            fullWidth
                            options={[{ clientId: "__new__", fullName: "➕ Créer un nouveau client" }, ...clientsList]}
                            getOptionLabel={(option) => {
                              if (!option) return ""
                              if (option.clientId === "__new__") return option.fullName
                              if (option.fullName && (option.email || option.phoneNumber)) {
                                return `${option.fullName} (${option.phoneNumber || option.email || ""})`
                              }
                              return option.fullName || option.email || option.phoneNumber || ""
                            }}
                            noOptionsText="Aucun client trouvé"
                            value={clientsList.find((c) => c.clientId === value) || selectedClient || null}
                            onInputChange={(e, newInputValue, reason) => {
                              if (reason === "input") setSearchTerm(newInputValue)
                              if (reason === "clear" || newInputValue === "") {
                                setSelectedClient(null)
                                onChange("")
                              }
                            }}
                            onChange={(e, selectedOption) => {
                              if (!selectedOption) return

                              if (selectedOption.clientId === "__new__") {
                                e.preventDefault()
                                setSelectedClient(null)
                                openClientModal(null)
                                return
                              }
                              handleClientSelect(selectedOption)
                              onChange(selectedOption.clientId)
                            }}
                            renderOption={(props, option) => (
                              <li {...props}>
                                {option.clientId === "__new__" ? (
                                  <em style={{ color: "#1976d2", fontWeight: 500 }}>{option.fullName}</em>
                                ) : (
                                  <div>
                                    <strong>{option.fullName}</strong>
                                    <br />
                                    <small style={{ color: "#777" }}>{option.phoneNumber || option.email || ""}</small>
                                  </div>
                                )}
                              </li>
                            )}
                            renderInput={(params) => (
                              <MDInput
                                {...params}
                                label="Client"
                                placeholder="Rechercher par nom, téléphone ou email"
                                variant="standard"
                                fullWidth
                                margin="normal"
                                error={Boolean(error)}
                                helperText={error?.message}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {selectedClient && (
                                        <InputAdornment position="end">
                                          <Tooltip title="Voir les détails">
                                            <IconButton onClick={() => openClientDetailsModal(selectedClient)}>
                                              <InfoIcon />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Modifier">
                                            <IconButton onClick={() => openClientModal(selectedClient)}>
                                              <EditIcon />
                                            </IconButton>
                                          </Tooltip>
                                          {/* <Tooltip title="Supprimer">
                                            <IconButton onClick={() => handleDeleteClient(selectedClient)}>
                                              <DeleteIcon />
                                            </IconButton>
                                          </Tooltip> */}
                                        </InputAdornment>
                                      )}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        )}
                      />

                      {/* client name */}
                      <Controller
                        control={control}
                        name="clientName"
                        defaultValue=""
                        rules={{
                          required: {
                            value: true,
                            message: "Le nom du client est requis",
                          },
                        }}
                        render={({ field: { ref, ...field }, fieldState: { error } }) => (
                          <MDInput
                            fullWidth
                            variant="standard"
                            margin="normal"
                            type="text"
                            id="id-client_name"
                            label="Nom du client"
                            error={Boolean(error)}
                            helperText={error && error.message}
                            {...field}
                          />
                        )}
                      />

                      {/* phone number */}
                      <Controller
                        control={control}
                        name="clientPhoneNumber"
                        defaultValue=""
                        rules={{
                          required: {
                            value: true,
                            message: "Le numéro de téléphone est requis",
                          },
                        }}
                        render={({ field: { ref, ...field }, fieldState: { error } }) => (
                          <MDInput
                            fullWidth
                            variant="standard"
                            id="id-client_phone_num"
                            label="Téléphone"
                            type="number"
                            margin="normal"
                            error={Boolean(error)}
                            helperText={error && error.message}
                            {...field}
                          />
                        )}
                      />

                      {/* state */}
                      <Controller
                        control={control}
                        name="state"
                        rules={{
                          validate: () => state !== "-" || "L'état est requis",
                        }}
                        render={({ field: { ref, onChange, value, ...field }, fieldState: { error } }) => (
                          <FormControl
                            fullWidth
                            margin="normal"
                            variant="standard"
                            sx={{ pt: 1, minWidth: 120 }}
                            error={Boolean(error) && state === "-"}
                          >
                            <InputLabel id="id-state-label">État</InputLabel>
                            <Select
                              labelId="id-state-label"
                              id="id-state"
                              value={state}
                              onChange={handleChangeState}
                              label="État"
                              size="medium"
                              {...field}
                            >
                              <MenuItem value="-">
                                <em>Aucune</em>
                              </MenuItem>
                              {governorates?.map((governorateItem) => (
                                <MenuItem key={governorateItem} value={governorateItem}>
                                  {governorateItem}
                                </MenuItem>
                              ))}
                            </Select>
                            {Boolean(error) && state === "-" && <FormHelperText>{error?.message}</FormHelperText>}
                          </FormControl>
                        )}
                      />

                      {/* city */}
                      <Controller
                        control={control}
                        name="city"
                        rules={{
                          validate: () => city !== "-" || "La ville est obligatoire",
                        }}
                        render={({ field: { ref, onChange, value, ...field }, fieldState: { error } }) => (
                          <FormControl
                            fullWidth
                            margin="normal"
                            variant="standard"
                            sx={{ pt: 1, minWidth: 120 }}
                            error={Boolean(error) && city === "-"}
                          >
                            <InputLabel id="id-city-label">Ville</InputLabel>
                            <Select
                              labelId="id-city-label"
                              id="id-city"
                              value={city}
                              onChange={handleChangeCity}
                              label="Ville"
                              size="medium"
                              {...field}
                            >
                              {cities?.length === 0 && (
                                <MenuItem value="-">
                                  <em>Aucune</em>
                                </MenuItem>
                              )}
                              {cities?.map((cityItem) => (
                                <MenuItem key={cityItem} value={cityItem}>
                                  {cityItem === "-" ? "Aucune" : cityItem}
                                </MenuItem>
                              ))}
                            </Select>
                            {Boolean(error) && city === "-" && <FormHelperText>{error?.message}</FormHelperText>}
                          </FormControl>
                        )}
                      />

                      {/* adress */}
                      <Controller
                        control={control}
                        name="address"
                        defaultValue=""
                        rules={{
                          required: {
                            value: true,
                            message: "L'adresse est obligatoire",
                          },
                        }}
                        render={({ field: { ref, ...field }, fieldState: { error } }) => (
                          <MDInput
                            fullWidth
                            variant="standard"
                            id="id-address"
                            label="Adresse"
                            type="text"
                            margin="normal"
                            error={Boolean(error)}
                            helperText={error && error.message}
                            {...field}
                          />
                        )}
                      />

                      {/* comments */}
                      <Controller
                        control={control}
                        name="comments"
                        defaultValue=""
                        rules={{
                          required: {
                            value: true,
                            message: "Commentaire est requis",
                          },
                        }}
                        render={({ field: { ref, ...field }, fieldState: { error } }) => (
                          <MDInput
                            fullWidth
                            variant="standard"
                            id="id-comments"
                            type="text"
                            label="Commentaire"
                            margin="normal"
                            error={Boolean(error)}
                            helperText={error && error.message}
                            {...field}
                          />
                        )}
                      />

                      {/* description */}
                      {/* <Controller
                        control={control}
                        name="description"
                        defaultValue=""
                        rules={{
                          required: {
                            value: true,
                            message: "La description est obligatoire",
                          },
                        }}
                        render={({ field: { ref, ...field }, fieldState: { error } }) => (
                          <MDInput
                            fullWidth
                            variant="outlined"
                            type="text"
                            id="id-description"
                            label="Description"
                            margin="normal"
                            multiline
                            rows={5}
                            error={Boolean(error)}
                            helperText={error && error.message}
                            {...field}
                          />
                        )}
                      /> */}

                      <FormControl component="fieldset" margin="normal" fullWidth>
                        <FormLabel
                          component="legend"
                          color="info"
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: 400,
                          }}
                        >
                          Souhaitez-vous échanger le produit?
                        </FormLabel>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <MDTypography variant="body2" fontWeight="regular">
                            Non
                          </MDTypography>
                          <AntSwitch checked={isExchangeChecked} onChange={handleChangeExchange} />
                          <MDTypography variant="body2" fontWeight="regular">
                            Oui
                          </MDTypography>
                        </Stack>
                      </FormControl>
                      {/* actions */}
                      <MDBox px={2} pb={4} gap={2} display="flex" alignItems="center">
                        {isLoading ? (
                          <CircularProgress color="secondary" />
                        ) : (
                          <>
                            <MDButton variant="contained" color="light" onClick={handleResetForm}>
                              Annuler
                            </MDButton>
                            {isPackageCreateForm ? (
                              <MDButton type="submit" variant="gradient" color="info" disabled={isLoading}>
                                Créer
                              </MDButton>
                            ) : (
                              <MDButton type="submit" variant="gradient" color="info" disabled={isLoading}>
                                Modifier
                              </MDButton>
                            )}
                          </>
                        )}
                      </MDBox>
                    </MDBox>
                  </Card>
                </Grid>
              </Grid>
            </MDBox>
          </DashboardLayout>
        </>
      )}

      <ClientCreateModal
        open={isClientModalOpen}
        onClose={closeClientModal}
        onSave={handleClientModalSave}
        governorates={governorates}
        fullGovernorates={fullGovernorates}
        client={selectedClient}
      />

      <ClientDetailsModal
        open={isClientDetailsOpen}
        onClose={closeClientDetailsModal}
        client={selectedClient}
        governorates={governorates}
        fullGovernorates={fullGovernorates}
      />

      <MDSnackbar
        color={snackbarObject.color}
        icon={snackbarObject.icon}
        title={snackbarObject.title}
        content={snackbarObject.content}
        open={snackbarObject.open}
        close={handleCloseSnackbar}
        dateTime=""
      />
    </>
  )
}
